import { useState } from 'react';

import { Button, Icon } from '@sb/design-system';
import { useFeatureFlag } from '@sbrc/hooks';

export interface FallbackProps {
  error?: Error;
  resetErrorBoundary: () => void;
  ErrorContainer: React.ComponentType<React.PropsWithChildren>;
}

export const FallbackRender = ({
  error,
  resetErrorBoundary,
  ErrorContainer,
}: FallbackProps & {
  ErrorContainer: React.ComponentType<React.PropsWithChildren>;
}) => {
  const [isStackTraceExpanded, setIsStackTraceExpanded] = useState(false);
  const showStackTrace = useFeatureFlag('showStackTraceInErrorBoundary');

  return (
    <ErrorContainer>
      <div className="tw-p-16 tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
        <Icon kind="xmarkCircle" className="tw-text-red tw-icon-42 tw-mb-12" />
        <p className="tw-mb-8">Error rendering component</p>
        <Button variant="Flat" onClick={resetErrorBoundary}>
          Tap to reload
        </Button>
        {showStackTrace && error && (
          <>
            <Button
              variant="Flat"
              onClick={() => setIsStackTraceExpanded(!isStackTraceExpanded)}
              className="tw-flex tw-items-center tw-gap-4 tw-text-metal-60 hover:tw-text-metal-80 dark:tw-text-metal-40 dark:hover:tw-text-metal-20 tw-transition-colors"
            >
              <Icon
                kind={isStackTraceExpanded ? 'chevronDown' : 'chevronRight'}
                className="tw-icon-12"
              />
              <span className="tw-text-10">
                {isStackTraceExpanded ? 'Hide' : 'Show'} Details
              </span>
            </Button>
            <div className="tw-w-full tw-mx-24 tw-max-w-2xl tw-mb-8">
              {isStackTraceExpanded && (
                <div className="tw-mt-8 tw-p-12 tw-bg-metal-10 dark:tw-bg-metal-90 tw-rounded tw-border tw-border-metal-30 dark:tw-border-metal-70 tw-overflow-auto tw-max-h-[400px] tw-rounded-10">
                  <div className="tw-text-xs tw-font-mono tw-text-metal-80 dark:tw-text-metal-20">
                    <div className="tw-font-semibold tw-mb-4 tw-text-red">
                      {error.name}: {error.message}
                    </div>
                    <pre className="tw-whitespace-pre-wrap tw-break-words">
                      {error.stack}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </ErrorContainer>
  );
};
